import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { useEffect, useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { getSystemLoggingStats } from '../../../../../services/SystemLoggingService';
import {
  setStatsError,
  setStatsLoading,
  setStatsSuccess
} from '../../../../../store/slices/systemLoggingSlice';
import { LOG_ENTRY_TYPE_LABELS, LogEntryType } from '../../../../../types/SystemLogging.types';
import { OVERVIEW_CHART_COLORS } from './OverviewChartCard.constant';

ChartJS.register(ArcElement, Tooltip, Legend);

export function OverviewChartCard() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { stats } = useAppSelector((state) => state.systemLogging);

  useEffect(() => {
    const fetchStats = async () => {
      if (!id) return;

      try {
        dispatch(setStatsLoading(true));
        const data = await getSystemLoggingStats(id);
        dispatch(setStatsSuccess(data));
      } catch (error: any) {
        dispatch(setStatsError(error.message || 'Failed to fetch stats'));
      }
    };

    fetchStats();
  }, [id, dispatch]);

  // Biggest slice first - reads better on a doughnut than insertion order.
  const typeEntries = useMemo(
    () =>
      Object.entries(stats?.byType || {})
        .filter(([, count]) => count > 0)
        .sort(([, a], [, b]) => b - a) as [LogEntryType, number][],
    [stats?.byType]
  );
  const hasNoData = typeEntries.length === 0;

  const data = hasNoData
    ? {
        labels: ['No Data'],
        datasets: [
          {
            data: [100],
            backgroundColor: [OVERVIEW_CHART_COLORS.noData],
            borderWidth: 0,
            cutout: '70%'
          }
        ]
      }
    : {
        labels: typeEntries.map(([type]) => LOG_ENTRY_TYPE_LABELS[type]),
        datasets: [
          {
            data: typeEntries.map(([, count]) => count),
            backgroundColor: typeEntries.map(
              (_, index) =>
                OVERVIEW_CHART_COLORS.palette[index % OVERVIEW_CHART_COLORS.palette.length]
            ),
            borderWidth: 0,
            cutout: '70%'
          }
        ]
      };

  const options = {
    layout: {
      padding: 12
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: !hasNoData
      }
    },
    maintainAspectRatio: true,
    responsive: true,
    ...(hasNoData && { events: [] })
  };

  return (
    <div className="flex flex-col rounded-lg bg-white py-6 px-4 gap-3 ring-1 ring-[#DADADD] z-[1]">
      <span className="font-inter font-medium text-xs leading-4 tracking-[0.05em] uppercase text-[#767681] text-left">
        OVERVIEW
      </span>
      <div className="flex flex-col items-center gap-3 justify-between flex-1 overflow-hidden">
        <div className="w-[130px] h-[130px] flex-shrink-0 relative z-50">
          <Doughnut data={data} options={options} />
          {hasNoData && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="font-inter font-medium text-sm leading-5 text-center text-[#111827] whitespace-nowrap">
                No data available
              </span>
            </div>
          )}
        </div>
        {hasNoData ? (
          <div className="h-[10px]"></div>
        ) : (
          <div className="flex gap-2 justify-center flex-wrap">
            {typeEntries.map(([type], index) => (
              <div key={type} className="flex items-center gap-1">
                <div
                  className="w-6 h-2 rounded-sm"
                  style={{
                    backgroundColor:
                      OVERVIEW_CHART_COLORS.palette[index % OVERVIEW_CHART_COLORS.palette.length]
                  }}
                ></div>
                <span className="font-inter text-[10px] leading-[10px] text-gray-700">
                  {LOG_ENTRY_TYPE_LABELS[type]}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
